import React, { useState } from 'react';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Paper,
  Divider,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Help as HelpIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Layers as LayersIcon,
  Route as PathIcon,
  Rule as RuleIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Preview as PreviewIcon,
  Download as ExportIcon,
  Palette as ColorIcon,
  AutoFixHigh as GenerateIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { theme, commonStyles } from '../../styles/theme';

const HelpSystem = ({ open, onClose, userMode = 'simple' }) => {
  const [selectedSection, setSelectedSection] = useState('overview');

  const helpSections = {
    overview: {
      title: 'GraveForge Overview',
      icon: <HelpIcon />,
      content: {
        simple: `
# Welcome to GraveForge!

GraveForge is a free NFT collection generator that helps you create unique digital art collections. Deploy through Gravemint when you're ready to mint.

## Quick Start:
1. **Create Layers** - Upload your art pieces (backgrounds, characters, accessories)
2. **Set Percentages** - Control how often each piece appears
3. **Generate Collection** - Preview and create your unique NFT collection
4. **Export to Gravemint** - Deploy your collection through the Gravemint launchpad

## Free Tier:
- Unlimited projects and layers
- Full path and rules engine
- Preview generation
- Project import/export (.bfp files)

## Deployment:
- Mint and deploy through Gravemint (our launchpad)
- Multi-chain support: Solana, Ethereum, Polygon, Base, and more
- On-chain metadata and IPFS/Arweave uploads handled by Gravemint
        `,
        advanced: `
# GraveForge Advanced Features

## Core Concepts:
- **Layers**: Categories of art (Background, Character, Hat, etc.)
- **Images**: Individual art pieces within each layer
- **Paths**: Control which combinations can appear together
- **Rules**: Block or force specific combinations
- **Rarity**: Control how rare certain traits are

## Workspace Navigation:
- **Build** - Layers, Paths, Rules
- **Generate** - Metadata, Preview, Custom Builder, Generate
- **Analyze** - Path Analytics, Path Testing, Rarity Analysis, Analytics
- **Project** - Team, Notes, Activity, Payments, Security, Settings

## Generation Process:
1. **Layer Setup** - Define your art categories
2. **Path Mapping** - Create decision trees for combinations
3. **Rule Engine** - Set constraints and requirements
4. **Rarity System** - Control trait distribution
5. **Batch Generation** - Create thousands of unique NFTs
6. **Export to Gravemint** - Deploy to any supported blockchain
        `
      }
    },
    layers: {
      title: 'Layer Management',
      icon: <LayersIcon />,
      content: {
        simple: `
# Managing Layers 📚

## What are Layers?
Layers are categories of your art pieces. Think of them like folders:
- **Background** - Sky, landscape, patterns
- **Character** - Main character or object
- **Accessories** - Hats, glasses, jewelry

## How to Use:
1. **Create Layer** - Click "Add Layer" and give it a name
2. **Upload Images** - Drag and drop your art files
3. **Set Percentages** - Control how often each image appears
4. **Order Matters** - Drag layers to reorder (background first, accessories last)

## Tips:
- Use PNG files with transparency for best results
- Keep consistent image sizes (recommended: 1000x1000px)
- Name your files clearly for easy organization
        `,
        advanced: `
# Advanced Layer Management 🎯

## Layer Properties:
- **Order Index**: Controls rendering order (0 = background)
- **Rarity Mode**: Percentage vs Rarity tier system
- **Group Assignment**: Organize layers into logical groups
- **Blend Modes**: Control how layers composite together

## Rarity Systems:
### Percentage Mode:
- Direct control over appearance rates
- Each image has a percentage weight
- Automatically normalizes to 100%

### Rarity Mode:
- Uses tier system (Common, Rare, Epic, etc.)
- Predefined rarity distributions
- Easier for large collections

## Advanced Features:
- **None Placeholders**: Allow layers to be empty
- **Conditional Layers**: Layers that depend on other selections
- **Layer Groups**: Organize related layers together
- **Batch Operations**: Edit multiple images at once
        `
      }
    },
    paths: {
      title: 'Path Mapping',
      icon: <PathIcon />,
      content: {
        simple: `
# Layer Order (Automatic) 🎨

## How It Works:
In Simple Mode, layers are automatically ordered from top to bottom in your layer list.

## Generation Flow:
1. **Background** (bottom layer)
2. **Character** (middle layer)
3. **Accessories** (top layer)
4. Each layer is applied in order

## No Path Mapping Needed:
- ✅ Layers stack automatically
- ✅ Order is based on your layer list
- ✅ All layers are always included
- ✅ Focus on creating art, not complex paths

## Tips:
- Arrange layers in the order you want them stacked
- Bottom layers appear behind top layers
- Drag and drop to reorder layers
        `,
        advanced: `
# Advanced Path Mapping 🗺️

## Path System Architecture:
- **Decision Trees**: Complex branching logic
- **Conditional Paths**: Paths that depend on previous selections
- **Path Groups**: Organize related paths together
- **Weighted Distribution**: Fine-tune generation probabilities

## Advanced Concepts:
### Path Groups:
- Group related paths for organization
- Apply bulk operations to entire groups
- Color-code for visual clarity

### Conditional Logic:
- Paths can be conditional on previous layer selections
- Create complex generation rules
- Support for AND/OR logic conditions

### Performance Optimization:
- Path validation ensures no dead ends
- Cycle detection prevents infinite loops
- Efficient pathfinding algorithms

## Best Practices:
- Keep paths simple initially, add complexity gradually
- Test generation with small batches first
- Use path groups for organization
- Document complex path logic
        `
      }
    },
    rules: {
      title: 'Rules Engine',
      icon: <RuleIcon />,
      content: {
        simple: `
# Rules Engine ⚖️

## What are Rules?
Rules control which combinations can appear together in your NFTs.

## Two Types:
### Block Rules (🚫):
- Prevent certain combinations
- Example: "Red Hat" cannot appear with "Blue Shirt"

### Force Rules (✅):
- Require certain combinations  
- Example: "Crown" must appear with "Royal Robe"

## How to Create:
1. Click "Create Rule"
2. Choose Block or Force
3. Select source layer/image (the trigger)
4. Select target layer/image (what gets affected)
5. Set probability (how often the rule applies)

## Important:
- Source layer must come BEFORE target layer in your generation order
- Rules only work from earlier layers to later layers
        `,
        advanced: `
# Advanced Rules Engine 🔧

## Rule Architecture:
- **Source/Target System**: Rules flow from earlier to later layers
- **Probability Weighting**: Fine-tune rule application rates
- **Rule Chains**: Complex multi-step rule sequences
- **Conditional Rules**: Rules that depend on multiple conditions

## Rule Types:
### Block Rules:
- Prevent incompatible combinations
- Support probability weighting
- Can target specific images or entire layers
- Useful for thematic consistency

### Force Rules:
- Ensure required combinations appear
- Higher probability = more enforcement
- Can create rare "legendary" combinations
- Useful for special trait sets

## Advanced Features:
### Rule Chains:
- Multi-step rule sequences
- "If A then B, if B then C" logic
- Create complex narrative combinations
- Support for branching logic

### Validation System:
- Real-time rule conflict detection
- Performance impact analysis
- Rule effectiveness reporting
- Automatic optimization suggestions

## Best Practices:
- Start with simple rules, add complexity gradually
- Test rules with small generation batches
- Monitor rule effectiveness in analytics
- Document complex rule interactions
        `
      }
    },
    payment: {
      title: 'Pricing & Deployment',
      icon: <PaymentIcon />,
      content: {
        simple: `
# Pricing & Deployment

## Free Tier:
GraveForge is free to use for building and previewing NFT collections:
- Unlimited projects and layers
- Full path and rules engine
- Preview generation
- Project import/export (.bfp files)

## Deploying Your Collection:
When you're ready to mint, export your collection to **Gravemint** (our launchpad):
1. Build your collection in GraveForge
2. Link your project to Gravemint
3. Configure mint settings in Gravemint
4. Launch on any supported blockchain

## Supported Chains (via Gravemint):
- Solana, Ethereum, Polygon, Base, Abstract, and more
- IPFS/Arweave metadata uploads
- Mint pages, allowlists, and revenue management
        `,
        advanced: `
# Pricing & Deployment Details

## Free Tier Includes:
- Unlimited projects, layers, paths, and rules
- Full generation engine with preview
- Project import/export with encryption
- Team collaboration and delegation
- Path analytics and visual testing
- Rarity analysis and reporting

## Gravemint Integration:
GraveForge connects to Gravemint for deployment:
- **Link Project**: Connect your GraveForge project to a Gravemint collection
- **Asset Transfer**: Generated images are uploaded to Gravemint's storage
- **Metadata**: On-chain metadata generated based on your GraveForge configuration
- **Multi-Chain**: Deploy to any chain Gravemint supports

## Storage:
- GraveForge uses Supabase Storage (default) or S3 for generated assets
- Generated files are retained for 48 hours after generation
- Export files (.bfp) are retained for 1 hour
- Use import/export to preserve your project permanently

## DEADS Token Benefits:
- Pay with DEADS for extra savings on premium features
- Stackable with volume tiers
- Automatic recognition at checkout
        `
      }
    },
    generation: {
      title: 'NFT Generation',
      icon: <GenerateIcon />,
      content: {
        simple: `
# Generating Your NFTs

## Before You Generate:
1. **Upload all your art** to layers
2. **Set up paths** from START to END (Advanced mode) or let layers auto-stack (Simple mode)
3. **Configure metadata** (name, description, symbol)
4. **Test with a small batch** first (try 10-50 NFTs)

## Generation Process:
1. Go to the **Generate** page
2. Enter how many NFTs you want
3. Choose generation settings (format, quality)
4. Click "Start Generation"
5. Wait for completion
6. Preview results and export to Gravemint

## What You Get:
- **Images**: PNG, JPG, WebP (or MP4/WebM/GIF for animated)
- **Metadata**: JSON files with trait information
- **Rarity Report**: Shows how rare each NFT is

## Tips:
- Start small to test your setup
- Use Preview to check individual combinations first
- Make sure you have enough credits
        `,
        advanced: `
# Advanced Generation Features ⚙️

## Generation Engine:
- **Deterministic Algorithm**: Same settings = same results
- **Collision Detection**: Prevents duplicate NFTs
- **Rarity Calculation**: Real-time rarity scoring
- **Batch Processing**: Efficient large-scale generation

## Generation Settings:
### Collection Size:
- Optimal sizes: 1K, 5K, 10K NFTs
- Collision probability calculations
- Rarity distribution analysis
- Performance optimization

### Quality Settings:
- **Image Resolution**: 1K, 2K, 4K output options
- **Compression**: Balance file size vs quality
- **Format Options**: PNG, WebP support
- **Metadata Standards**: OpenSea, Magic Eden compatible

## Advanced Features:
### Rarity Engine:
- **Statistical Rarity**: Based on trait frequency
- **Weighted Rarity**: Considers trait importance
- **Normalized Scoring**: 0-100 rarity scale
- **Rarity Tiers**: Automatic tier assignment

### Performance Optimization:
- **Parallel Processing**: Multi-threaded generation
- **Memory Management**: Efficient large batch handling
- **Progress Tracking**: Real-time generation status
- **Error Recovery**: Automatic retry on failures

## Analytics:
- Generation success rates
- Rarity distribution analysis
- Performance metrics
- Quality assurance reports
        `
      }
    },
    export: {
      title: 'Export & Download',
      icon: <ExportIcon />,
      content: {
        simple: `
# Exporting Your Collection 📦

## What You Can Export:
- **Images**: Your generated NFT artwork
- **Metadata**: Trait information for each NFT
- **Rarity Report**: Shows how rare each NFT is

## Export Formats:
- **ZIP File**: All images and metadata together
- **Individual Files**: Download specific NFTs
- **Metadata Only**: Just the JSON files

## File Structure:
\`\`\`
collection/
├── images/
│   ├── 1.png
│   ├── 2.png
│   └── ...
├── metadata/
│   ├── 1.json
│   ├── 2.json
│   └── ...
└── rarity-report.csv
\`\`\`

## Tips:
- Download immediately after generation
- Keep backups of your collections
- Check file integrity before uploading to marketplaces
        `,
        advanced: `
# Advanced Export Features 📊

## Export Formats:
### Standard Formats:
- **OpenSea Compatible**: Standard metadata format
- **Magic Eden**: Solana marketplace format
- **Custom JSON**: Flexible metadata structure
- **CSV Reports**: Spreadsheet-compatible data

### Image Formats:
- **PNG**: Lossless, transparency support
- **WebP**: Smaller files, modern browsers
- **JPEG**: Smallest files, no transparency
- **SVG**: Vector format (if applicable)

## Batch Operations:
### Selective Export:
- Export by rarity tiers
- Export specific trait combinations
- Export date ranges
- Custom filtering options

### Bulk Processing:
- Parallel download streams
- Resume interrupted downloads
- Integrity verification
- Automatic retry on failures

## Integration Features:
### Marketplace Preparation:
- **Metadata Validation**: Ensure marketplace compatibility
- **Image Optimization**: Optimal file sizes
- **Trait Standardization**: Consistent naming conventions
- **Rarity Integration**: Embed rarity scores

### API Access:
- **REST API**: Programmatic access to collections
- **Webhook Support**: Real-time export notifications
- **Batch APIs**: Bulk operations
- **Authentication**: Secure API access

## Quality Assurance:
- File integrity checks
- Metadata validation
- Image quality verification
- Completeness reporting
        `
      }
    },
    security: {
      title: 'Security Features',
      icon: <ShieldIcon />,
      content: {
        simple: `
# Security Features 🔒

## Steganography Watermarking
Embed invisible watermarks into your NFT images to prove authenticity.

### What is Steganography?
A technique that hides text inside images without any visible changes. The watermark is embedded at the pixel level and can only be extracted with special tools.

### Requirements:
- **Advanced Mode** - Must be enabled in project settings
- **Server-Side Rendering** - Only available with server generation
- **Configured in Security Page** - Navigate to Security menu to set up

### How to Use:
1. Switch project to Advanced Mode
2. Go to Security page in sidebar
3. Enable steganography
4. Configure watermark template
5. Use server-side generation

### Watermark Templates:
You can use placeholders that get replaced automatically:
- \`{project}\` - Your project name
- \`{id}\` - NFT token number
- \`{index}\` - Generation sequence
- \`{timestamp}\` - When it was created
- \`{hash}\` - Unique identifier

### Example:
Template: \`{project} - #{id}\`
Result: \`My Collection - #42\`

### Benefits:
- Proves authenticity
- Tracks unauthorized copies
- Invisible to viewers
- Cannot be removed easily
        `,
        advanced: `
# Advanced Security Features 🛡️

## Steganography Watermarking

### Technical Details:
- **Algorithm**: LSB (Least Significant Bit) modification
- **Capacity**: Up to 50 characters per image
- **Imperceptibility**: Changes are invisible to human eye
- **Robustness**: Survives PNG compression and resizing
- **Extraction**: Requires decoding tool with algorithm knowledge

### Implementation:
The system modifies the least significant bit of RGB pixel values to encode text. This creates imperceptible changes (±1 color value) that embed your watermark data.

### Placeholder System:
Dynamic placeholders are replaced during generation:

**Available Placeholders:**
- \`{project}\` - Project name from database
- \`{id}\` - NFT token ID (1, 2, 3...)
- \`{index}\` - Generation sequence number
- \`{timestamp}\` - ISO 8601 timestamp
- \`{hash}\` - SHA-256 hash of project+token

**Example Templates:**
\`\`\`
{project} © 2025 - #{id}
Token {id} | {timestamp}
{project}-{hash}
\`\`\`

### Security Considerations:
1. **Server-Side Only**: Watermarks applied on server before delivery
2. **Unique Per NFT**: Each NFT gets unique watermark
3. **Tamper-Evident**: Modifying image corrupts watermark
4. **Extraction Tools**: Keep decoder private for security
5. **Legal Protection**: Watermark serves as proof of ownership

### Use Cases:
- **Authenticity Verification**: Prove NFTs are genuine
- **Copyright Protection**: Track unauthorized distribution
- **Forensic Analysis**: Identify source of leaks
- **Licensing**: Embed license information
- **Attribution**: Permanent creator attribution

### Best Practices:
- Keep watermark text concise (under 50 chars)
- Use consistent format across collection
- Document your watermark scheme
- Test extraction before full generation
- Store decoder tool securely

### Limitations:
- Only works with server-side generation
- Requires Advanced mode
- Not available in client-side generation
- Cannot be added to existing images
- Watermark is permanent once embedded

## Future Security Features:
- Digital signatures for metadata
- Access control and permissions
- Audit logs for all changes
- End-to-end encryption for sensitive data
        `
      }
    },
    profile: {
      title: 'Profile & Email Notifications',
      icon: <SettingsIcon />,
      content: {
        simple: `
# Profile Settings & Email Notifications 📧

## Profile Setup
Customize your GraveForge profile to personalize your experience and receive important updates.

### How to Access:
1. Click the **person icon** in the top navbar (next to wallet)
2. Or select **"Profile Settings"** from the sidebar menu

### Profile Information:
- **Username** - Display name shown in emails and notifications
- **Email Address** - Required for email notifications
- **Wallet Address** - Automatically linked to your connected wallet

## Email Notifications

### What You'll Receive:
Professional email notifications when your NFT generation jobs complete, including:
- Project name and NFT count
- Generation duration and completion time
- Who initiated the generation
- Download link (for server-side generation)
- Download expiry warning

### Notification Types:

#### 1. My Own Projects
Get notified when YOUR generation jobs complete.
- ✅ **Server-Side Generation** - Includes download link
- ✅ **Client-Side Generation** - Confirmation only (auto-downloaded in browser)
- Perfect for tracking your own work

#### 2. Team Projects
Get notified when TEAM MEMBERS complete generation jobs.
- ✅ **Server-Side Only** - Includes download link for team access
- ❌ **Not for Client-Side** - Team can't download browser-generated files
- Great for collaborative projects

### Email Requirements:
- Must provide valid email address in Profile Settings
- Enable desired notification checkboxes
- Email sent from: graveforge@solanadeads.com

### Smart Features:
- **No Duplicates** - If you're the owner AND have team notifications enabled, you only get 1 email
- **Instant Delivery** - Emails sent immediately upon job completion
- **Professional Design** - Branded emails with GraveForge theme
- **Secure** - Only recipients with enabled notifications receive emails

## Tips:
- Set up your profile BEFORE generating to receive notifications
- Use a valid email address you check regularly
- Enable "My Own Projects" to track all your generations
- Enable "Team Projects" if you collaborate with others
- Update settings anytime from Profile Settings
        `,
        advanced: `
# Advanced Profile & Email System 🔔

## Email Notification System

### Recipient Logic:

#### Server-Side Generation:
\`\`\`javascript
Recipients = {
  Owner 
  Team Members 
  Initiator 
}

#### Client-Side Generation:
\`\`\`javascript
Recipients = {
  Initiator only
// No team notifications (can't download)
\`\`\`

### Performance:
- Async email sending (non-blocking)
- Batch recipient processing
- Cached Resend client
- Minimal database queries

### Privacy & Security:
- Emails only to opted-in users
- No email sharing between users
- GDPR-compliant unsubscribe
        `
      }
    }
  };

  const renderContent = (content) => {
    const text = content[userMode] || content.simple;
    
    // Simple markdown-like rendering
    return text.split('\n').map((line, index) => {
      if (line.startsWith('# ')) {
        return (
          <Typography key={index} variant="h4" sx={{ color: theme.colors.primary, mt: 2, mb: 1 }}>
            {line.substring(2)}
          </Typography>
        );
      }
      if (line.startsWith('## ')) {
        return (
          <Typography key={index} variant="h5" sx={{ color: theme.colors.primary, mt: 2, mb: 1 }}>
            {line.substring(3)}
          </Typography>
        );
      }
      if (line.startsWith('### ')) {
        return (
          <Typography key={index} variant="h6" sx={{ color: '#ef4444', mt: 1, mb: 0.5 }}>
            {line.substring(4)}
          </Typography>
        );
      }
      if (line.startsWith('```')) {
        return null; // Skip code block markers for now
      }
      if (line.trim() === '') {
        return <Box key={index} sx={{ height: 8 }} />;
      }
      
      return (
        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
          {line}
        </Typography>
      );
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', md: '600px' },
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          color: 'white',
          borderLeft: '2px solid #dc2626'
        }
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ color: theme.colors.primary }}>
            🆘 GraveForge Help
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={userMode === 'simple' ? 'Simple Mode' : 'Advanced Mode'} 
              size="small"
              sx={{ 
                bgcolor: userMode === 'simple' ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)',
                color: userMode === 'simple' ? '#00ff00' : '#ffaa00'
              }}
            />
            <IconButton onClick={onClose} sx={{ color: theme.colors.primary }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Sidebar */}
          <Box sx={{ width: 200, mr: 2 }}>
            <List dense>
              {Object.entries(helpSections).map(([key, section]) => (
                <ListItem
                  key={key}
                  button
                  selected={selectedSection === key}
                  onClick={() => setSelectedSection(key)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(220, 38, 38, 0.2)',
                      '&:hover': {
                        bgcolor: 'rgba(220, 38, 38, 0.3)',
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: theme.colors.primary, minWidth: 32 }}>
                    {section.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={section.title}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Paper sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
              {renderContent(helpSections[selectedSection].content)}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default HelpSystem;
